import Box from "@material-ui/core/Box";
import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import React, { useEffect, useState } from "react";
import InternalMessageType from "./InternalMessageType.model";
import MessageType from "./MessageType.model";

const useStyles = makeStyles({
  progressRoot: {
    height: "2px",
    position: "absolute",
    width: "calc(100% - 32px)",
    bottom: "8px",
    left: "16px",
  },
  progressColorPrimary: {
    backgroundColor: "rgba(255, 255, 255, 0.32)",
  },
  progressBarColorPrimary: {
    backgroundColor: "rgba(255, 255, 255, 0.7)",
  },
  cursorPointer: {
    cursor: "pointer",
  },
});

const DURATIONS_DEFAULT_MS: Record<MessageType, number> = {
  error: 5000,
  info: 5000,
  success: 3000,
  warning: 5000,
};

const INTERVAL_MS = 100;

const getStepSize = (duration: number, interval: number): number => {
  return 100 / (duration / interval);
};

interface MessageProps {
  message: InternalMessageType;
  removeMessage: (messageId: string) => void;
}

const Message: React.FC<MessageProps> = ({ message, removeMessage }) => {
  const { id, duration: messageDuration, type, text, autoHide } = message;
  const [progress, setProgress] = useState(100);
  const [stopped, setStopped] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>();
  const hasProgress = autoHide && !stopped;

  const classes = useStyles();

  const resetProgress = () => {
    if (timer) {
      clearInterval(timer);
      setTimer(null);
      setProgress(100);
    }
  };

  const startTimer = () => {
    const newTimer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress === 0) {
          clearInterval(newTimer);
          setTimeout(() => removeMessage(id), 500);
          return 0;
        }
        return Math.max(0, prevProgress - getStepSize(messageDuration || DURATIONS_DEFAULT_MS[type], INTERVAL_MS));
      });
    }, INTERVAL_MS);
    setTimer(newTimer);
  };

  const handleMessageClick = () => {
    if (hasProgress) {
      setStopped(true);
      resetProgress();
    }
  };

  const handleMessageMouseEnter = () => {
    if (hasProgress) {
      resetProgress();
    }
  };

  const handleMessageMouseLeave = () => {
    if (hasProgress) {
      startTimer();
    }
  };

  useEffect(() => {
    if (hasProgress) {
      startTimer();
    }
  }, []);

  return (
    <Box
      className={classes.cursorPointer}
      marginTop={1}
      onClick={handleMessageClick}
      onMouseEnter={handleMessageMouseEnter}
      onMouseLeave={handleMessageMouseLeave}
      position="relative"
    >
      <Alert severity={type || "info"} variant="filled">
        <Box display="flex" flexDirection="row" alignItems="center">
          <span>{text || ""}</span>
          <CloseIcon
            onClick={() => removeMessage(id)}
            style={{
              cursor: "pointer",
              fontSize: "22px",
              height: "22px",
              marginLeft: "8px",
              width: "22px",
            }}
          />
        </Box>
      </Alert>
      {hasProgress && (
        <LinearProgress
          classes={{
            root: classes.progressRoot,
            colorPrimary: classes.progressBarColorPrimary,
            barColorPrimary: classes.progressBarColorPrimary,
          }}
          variant="determinate"
          value={progress}
        />
      )}
    </Box>
  );
};

export default Message;
